import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal } from '../common/UIComponents';
import {
  PRODUCT_FEEDBACK_LIMITS,
  type ProductFeedbackCategory,
  type ProductFeedbackCreateInput,
  type ProductFeedbackDiagnosticsPayload,
  type ProductFeedbackRuntimeSnapshot,
} from '../../types/productFeedback';
import {
  createProductFeedbackReport,
  readCanonicalProductFeedbackReport,
  readProductFeedbackReportFromServer,
  type ProductFeedbackCreateResult,
} from '../../services/productFeedbackService';
import type { ProductFeedbackDiagnosticsHealthCheckResult } from '../../services/productFeedbackDiagnostics';
import {
  authorizeProductFeedbackAttachmentUpload,
  finalizeProductFeedbackAttachment,
  inspectProductFeedbackAttachmentObject,
  uploadProductFeedbackScreenshot,
  type ProductFeedbackAttachmentFinalizeResult,
} from '../../services/productFeedbackAttachmentService';
import {
  preprocessProductFeedbackScreenshot,
  type ProductFeedbackProcessedScreenshot,
} from '../../utils/productFeedbackImagePreprocessor';
import { createProductFeedbackClientRequestId } from '../../utils/productFeedbackRuntime';
import { PRODUCT_FEEDBACK_CATEGORY_OPTIONS } from '../../utils/productFeedbackUi';

interface ProductFeedbackReporterProps {
  diagnostics: ProductFeedbackDiagnosticsPayload;
  runtime: ProductFeedbackRuntimeSnapshot;
  reporterUid: string;
  triggerRef: React.RefObject<HTMLElement>;
  onRefreshSnapshot: () => void;
  onCheckDiagnostics: () => ProductFeedbackDiagnosticsHealthCheckResult;
  onViewReport: (reportId: string) => void;
  onClose: () => void;
}

type FormErrors = Partial<Record<'category' | 'description', string>>;
type AttachmentRetryMode = 'upload' | 'inspect' | 'finalize' | 'readback';
export type ProductFeedbackAttachmentFailureStage =
  | 'authorize-upload'
  | 'upload'
  | 'inspect-object'
  | 'finalize'
  | 'canonical-readback';
interface ProductFeedbackAttachmentFailure {
  stage: ProductFeedbackAttachmentFailureStage;
  code: string;
}
type PendingStage =
  | 'preparing'
  | 'saving'
  | 'confirming-report'
  | 'authorizing-upload'
  | 'checking-object'
  | 'uploading'
  | 'finalizing'
  | 'confirming-attachment';

interface AttachmentDraft {
  file: File;
  processed: ProductFeedbackProcessedScreenshot | null;
  previewUrl: string | null;
  error: string | null;
}

const PENDING_LABELS: Record<PendingStage, string> = {
  preparing: 'Przygotowywanie zrzutu…',
  saving: 'Zapisywanie raportu…',
  'confirming-report': 'Potwierdzanie zapisu raportu…',
  'authorizing-upload': 'Autoryzowanie wysyłania zrzutu…',
  'checking-object': 'Sprawdzanie zrzutu…',
  uploading: 'Wysyłanie zrzutu…',
  finalizing: 'Weryfikowanie zrzutu…',
  'confirming-attachment': 'Potwierdzanie canonical odczytu…',
};

const SAFE_ATTACHMENT_FAILURE_CODES = new Set([
  'cancelled',
  'deadline-exceeded',
  'failed-precondition',
  'internal',
  'not-found',
  'permission-denied',
  'resource-exhausted',
  'retry-limit-exceeded',
  'unauthenticated',
  'unavailable',
  'unknown',
]);

export const normalizeProductFeedbackAttachmentFailureCode = (error: unknown): string => {
  const rawCode =
    typeof (error as { code?: unknown })?.code === 'string'
      ? String((error as { code: string }).code)
          .trim()
          .toLowerCase()
      : '';
  const code = rawCode.split('/').pop() || '';
  if (code === 'unauthorized') return 'permission-denied';
  if (code === 'object-not-found') return 'not-found';
  return SAFE_ATTACHMENT_FAILURE_CODES.has(code) ? code : 'unknown';
};

class AttachmentOperationError extends Error {
  readonly failure: ProductFeedbackAttachmentFailure;

  constructor(stage: ProductFeedbackAttachmentFailureStage, cause: unknown) {
    super('Attachment operation failed.');
    this.name = 'AttachmentOperationError';
    this.failure = { stage, code: normalizeProductFeedbackAttachmentFailureCode(cause) };
  }
}

const fieldClassName =
  'mt-1 block min-h-11 w-full rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/80 px-3 py-2 text-sm text-primary-light-100 placeholder:text-primary-light-400 focus:border-transparent focus:ring-2 focus:ring-ratuj-300/70 disabled:cursor-not-allowed disabled:opacity-60';

const createExcludedDiagnostics = (
  frozen: ProductFeedbackDiagnosticsPayload
): ProductFeedbackDiagnosticsPayload => ({
  ...frozen,
  included: false,
  events: [],
  droppedEventCount: 0,
  clientRedactionCount: 0,
  serverRedactionCount: 0,
  omittedObjectCount: 0,
  bytes: 2,
  truncated: false,
});

const errorMessage = (error: unknown): string => {
  if (!window.navigator.onLine) {
    return 'Brak połączenia z siecią. Zgłoszenie nie zostało wysłane; formularz pozostał bez zmian.';
  }
  const code = String((error as { code?: unknown })?.code || '');
  if (code.includes('permission-denied') || code.includes('unauthenticated')) {
    return 'Uprawnienie do wysłania zgłoszenia nie jest już aktywne.';
  }
  return 'Nie udało się potwierdzić zapisu zgłoszenia. Dane formularza zostały zachowane.';
};

const formatSnapshotTime = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'czas nieznany';
  return new Intl.DateTimeFormat('pl-PL', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(parsed);
};

const useCompactReporter = (): boolean => {
  const [compact, setCompact] = useState(() => window.matchMedia('(max-width: 639px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 639px)');
    const update = () => setCompact(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);
  return compact;
};

export const ProductFeedbackReporter: React.FC<ProductFeedbackReporterProps> = ({
  diagnostics,
  runtime,
  reporterUid,
  triggerRef,
  onRefreshSnapshot,
  onCheckDiagnostics,
  onViewReport,
  onClose,
}) => {
  const titleId = useId();
  const categoryRef = useRef<HTMLSelectElement | null>(null);
  const confirmationCancelRef = useRef<HTMLButtonElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentGenerationRef = useRef(0);
  const previewUrlRef = useRef<string | null>(null);
  const [category, setCategory] = useState<ProductFeedbackCategory | ''>('');
  const [description, setDescription] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [reproductionNotes, setReproductionNotes] = useState('');
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [message, setMessage] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [pending, setPending] = useState(false);
  const [pendingStage, setPendingStage] = useState<PendingStage | null>(null);
  const [clientRequestId, setClientRequestId] = useState(createProductFeedbackClientRequestId);
  const [submissionInput, setSubmissionInput] = useState<ProductFeedbackCreateInput | null>(null);
  const [createResult, setCreateResult] = useState<ProductFeedbackCreateResult | null>(null);
  const [successReportId, setSuccessReportId] = useState<string | null>(null);
  const [attachmentDraft, setAttachmentDraft] = useState<AttachmentDraft | null>(null);
  const [savedReportId, setSavedReportId] = useState<string | null>(null);
  const [attachmentRetryMode, setAttachmentRetryMode] = useState<AttachmentRetryMode>('upload');
  const [finalizeResult, setFinalizeResult] =
    useState<ProductFeedbackAttachmentFinalizeResult | null>(null);
  const [attachmentConfirmed, setAttachmentConfirmed] = useState(false);
  const [attachmentFailure, setAttachmentFailure] =
    useState<ProductFeedbackAttachmentFailure | null>(null);
  const compact = useCompactReporter();
  const [optionalDetailsOpen, setOptionalDetailsOpen] = useState(
    () => !window.matchMedia('(max-width: 639px)').matches
  );
  const [diagnosticsPreviewOpen, setDiagnosticsPreviewOpen] = useState(false);
  const [diagnosticsHealth, setDiagnosticsHealth] = useState<'idle' | 'healthy' | 'unhealthy'>(
    'idle'
  );

  const attemptLocked = submissionInput !== null;
  const dirty =
    category !== '' ||
    description !== '' ||
    expectedBehavior !== '' ||
    actualBehavior !== '' ||
    reproductionNotes !== '' ||
    !includeDiagnostics ||
    attachmentDraft !== null;

  const diagnosticsPreview = useMemo(
    () =>
      diagnostics.events.map((event, index) => ({
        key: `${event.occurredAtClient}-${index}`,
        heading: `${event.level.toUpperCase()} · ${event.source}`,
        meta: `${event.occurredAtClient} · ${event.routePath}`,
        message: event.message,
        details: event.details,
        stack: event.stack,
      })),
    [diagnostics]
  );
  const hasOptionalDetails = Boolean(expectedBehavior || actualBehavior || reproductionNotes);

  const refreshSnapshot = () => {
    if (!includeDiagnostics || pending || attemptLocked) return;
    onRefreshSnapshot();
    setDiagnosticsPreviewOpen(false);
  };

  const checkDiagnostics = () => {
    try {
      const result = onCheckDiagnostics();
      setDiagnosticsHealth(result.healthy ? 'healthy' : 'unhealthy');
    } catch {
      setDiagnosticsHealth('unhealthy');
    }
  };

  const growTextarea = (element: HTMLTextAreaElement, compactMax = 128) => {
    element.style.height = 'auto';
    const maxHeight = compact ? compactMax : 176;
    element.style.height = `${Math.min(element.scrollHeight, maxHeight)}px`;
    element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  useEffect(() => {
    if (confirmDiscard) confirmationCancelRef.current?.focus();
  }, [confirmDiscard]);

  useEffect(
    () => () => {
      attachmentGenerationRef.current += 1;
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  const revokePreview = () => {
    if (!previewUrlRef.current) return;
    URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
  };

  const removeAttachment = () => {
    if (pending || attemptLocked) return;
    attachmentGenerationRef.current += 1;
    revokePreview();
    setAttachmentDraft(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const selectAttachment = async (file: File | undefined) => {
    if (!file || pending || attemptLocked) return;
    const generation = ++attachmentGenerationRef.current;
    revokePreview();
    setAttachmentDraft({ file, processed: null, previewUrl: null, error: null });
    setPendingStage('preparing');
    try {
      const processed = await preprocessProductFeedbackScreenshot(file);
      if (generation !== attachmentGenerationRef.current) return;
      const previewUrl = URL.createObjectURL(processed.blob);
      previewUrlRef.current = previewUrl;
      setAttachmentDraft({ file, processed, previewUrl, error: null });
    } catch (error) {
      if (generation !== attachmentGenerationRef.current) return;
      setAttachmentDraft({
        file,
        processed: null,
        previewUrl: null,
        error: error instanceof Error ? error.message : 'Nie udało się przygotować zrzutu.',
      });
    } finally {
      if (generation === attachmentGenerationRef.current) setPendingStage(null);
    }
  };

  const requestClose = () => {
    if (pending) return;
    if (confirmDiscard) {
      setConfirmDiscard(false);
      return;
    }
    if (successReportId || savedReportId || !dirty) {
      onClose();
      return;
    }
    setConfirmDiscard(true);
  };

  const verifyReadback = async (result: ProductFeedbackCreateResult) => {
    const canonical = await readProductFeedbackReportFromServer(result.reportId);
    if (
      canonical.reportId !== result.reportId ||
      canonical.createdBy !== reporterUid ||
      canonical.status !== result.status ||
      canonical.statusVersion !== result.statusVersion
    ) {
      throw new Error('Canonical Product Feedback readback does not match create result.');
    }
    return canonical.reportId;
  };

  const verifyFinalAttachment = async (
    reportId: string,
    expected: ProductFeedbackAttachmentFinalizeResult
  ) => {
    const canonical = await readCanonicalProductFeedbackReport(reportId);
    const attachment = canonical.attachment;
    if (
      canonical.id !== reportId ||
      canonical.createdBy !== reporterUid ||
      canonical.attachmentRequested !== true ||
      !attachment ||
      attachment.storagePath !== expected.storagePath ||
      attachment.generation !== expected.generation ||
      attachment.bytes !== expected.bytes ||
      attachment.contentType !== 'image/jpeg' ||
      attachment.attachedBy !== reporterUid
    ) {
      throw new Error('Canonical attachment readback does not match finalized object.');
    }
  };

  const runAttachment = async (
    reportId: string,
    mode: AttachmentRetryMode,
    knownFinalize: ProductFeedbackAttachmentFinalizeResult | null
  ) => {
    if (!attachmentDraft?.processed) throw new Error('Brak przygotowanego zrzutu.');
    let nextMode = mode;
    let verifiedFinalize = knownFinalize;

    if (nextMode === 'inspect' || nextMode === 'upload') {
      setPendingStage('authorizing-upload');
      try {
        await authorizeProductFeedbackAttachmentUpload(reportId);
      } catch (error) {
        throw new AttachmentOperationError('authorize-upload', error);
      }
    }
    if (nextMode === 'inspect') {
      setPendingStage('checking-object');
      let objectState: 'exists' | 'missing';
      try {
        objectState = await inspectProductFeedbackAttachmentObject(reportId);
      } catch (error) {
        throw new AttachmentOperationError('inspect-object', error);
      }
      nextMode = objectState === 'exists' ? 'finalize' : 'upload';
      setAttachmentRetryMode(nextMode);
    }
    if (nextMode === 'upload') {
      setPendingStage('uploading');
      try {
        await uploadProductFeedbackScreenshot(reportId, attachmentDraft.processed.blob);
        nextMode = 'finalize';
        setAttachmentRetryMode('finalize');
      } catch (error) {
        setAttachmentRetryMode('inspect');
        throw new AttachmentOperationError('upload', error);
      }
    }
    if (nextMode === 'finalize') {
      setPendingStage('finalizing');
      try {
        verifiedFinalize = await finalizeProductFeedbackAttachment(reportId);
        setFinalizeResult(verifiedFinalize);
        setAttachmentRetryMode('readback');
      } catch (error) {
        const code = String((error as { code?: unknown })?.code || '');
        setAttachmentRetryMode(code.includes('not-found') ? 'inspect' : 'finalize');
        throw new AttachmentOperationError('finalize', error);
      }
    }
    if (!verifiedFinalize) {
      throw new AttachmentOperationError('canonical-readback', new Error('missing-finalize'));
    }
    setPendingStage('confirming-attachment');
    try {
      await verifyFinalAttachment(reportId, verifiedFinalize);
    } catch (error) {
      throw new AttachmentOperationError('canonical-readback', error);
    }
    setAttachmentConfirmed(true);
    setAttachmentFailure(null);
    setSuccessReportId(reportId);
    setMessage(null);
    revokePreview();
  };

  const runSubmission = async (input: ProductFeedbackCreateInput, knownResult = createResult) => {
    if (pending) return;
    let confirmedReportId = savedReportId;
    setPending(true);
    setMessage(null);
    setAttachmentFailure(null);
    try {
      if (confirmedReportId && input.attachmentRequested) {
        await runAttachment(confirmedReportId, attachmentRetryMode, finalizeResult);
        return;
      }
      setPendingStage(knownResult ? 'confirming-report' : 'saving');
      const result = knownResult || (await createProductFeedbackReport(input));
      setCreateResult(result);
      setPendingStage('confirming-report');
      const reportId = confirmedReportId || (await verifyReadback(result));
      confirmedReportId = reportId;
      setSavedReportId(reportId);
      if (!input.attachmentRequested) {
        setSuccessReportId(reportId);
        setMessage(null);
      } else {
        await runAttachment(reportId, attachmentRetryMode, finalizeResult);
      }
    } catch (error) {
      if (confirmedReportId && error instanceof AttachmentOperationError) {
        setAttachmentFailure(error.failure);
      }
      setMessage(
        confirmedReportId
          ? `Raport ${confirmedReportId} został zapisany, ale zrzut ekranu nie jest jeszcze canonical potwierdzony.`
          : errorMessage(error)
      );
    } finally {
      setPending(false);
      setPendingStage(null);
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (pending || pendingStage === 'preparing' || successReportId) return;
    if (submissionInput) {
      void runSubmission(submissionInput);
      return;
    }

    const nextErrors: FormErrors = {};
    if (!category) nextErrors.category = 'Wybierz kategorię.';
    if (!description.trim()) nextErrors.description = 'Opisz problem lub sugestię.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !category) return;

    if (attachmentDraft && (!attachmentDraft.processed || attachmentDraft.error)) {
      setMessage('Najpierw wybierz poprawny zrzut lub usuń nieobsługiwany plik.');
      return;
    }
    const input: ProductFeedbackCreateInput = {
      clientRequestId,
      category,
      description: description.trim(),
      expectedBehavior: expectedBehavior.trim() || null,
      actualBehavior: actualBehavior.trim() || null,
      reproductionNotes: reproductionNotes.trim() || null,
      runtime,
      diagnostics: includeDiagnostics ? diagnostics : createExcludedDiagnostics(diagnostics),
      attachmentRequested: Boolean(attachmentDraft?.processed),
    };
    setSubmissionInput(input);
    void runSubmission(input, null);
  };

  const unlockForEditing = () => {
    if (pending) return;
    setSubmissionInput(null);
    setCreateResult(null);
    setSavedReportId(null);
    setAttachmentRetryMode('upload');
    setFinalizeResult(null);
    setAttachmentFailure(null);
    setMessage(null);
    setClientRequestId(createProductFeedbackClientRequestId());
  };

  return (
    <Modal
      isOpen
      onClose={requestClose}
      dialogRole={confirmDiscard ? 'alertdialog' : 'dialog'}
      ariaLabelledBy={titleId}
      initialFocusRef={categoryRef}
      returnFocusRef={triggerRef}
      placement="bottom-responsive"
      className="flex max-h-[92dvh] flex-col !overflow-hidden rounded-b-none sm:rounded-lg"
      dialogTestId="product-feedback-reporter"
    >
      {confirmDiscard ? (
        <div className="space-y-4 p-4 sm:p-5">
          <div>
            <h2 id={titleId} className="text-base font-semibold text-primary-light-100">
              Odrzucić niezapisane zgłoszenie?
            </h2>
            <p className="mt-2 text-sm text-primary-light-300">
              Treść formularza i zamrożony podgląd diagnostyczny zostaną usunięte z pamięci.
            </p>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              ref={confirmationCancelRef}
              type="button"
              variant="neutral"
              onClick={() => setConfirmDiscard(false)}
            >
              Wróć do formularza
            </Button>
            <Button type="button" variant="error" onClick={onClose}>
              Odrzuć zgłoszenie
            </Button>
          </div>
        </div>
      ) : successReportId ? (
        <div className="space-y-4 p-4 sm:p-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-ratuj-300">
              Zgłoszenie zapisane
            </p>
            <h2 id={titleId} className="mt-1 text-lg font-semibold text-primary-light-100">
              Raport {successReportId}
            </h2>
            <p className="mt-2 text-sm text-primary-light-300">
              Możesz śledzić stan zgłoszenia w historii swoich zgłoszeń.
            </p>
            {attachmentConfirmed && (
              <p className="mt-2 text-sm font-medium text-ratuj-200">Zrzut ekranu potwierdzony.</p>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="neutral" onClick={onClose}>
              Zamknij
            </Button>
            <Button type="button" onClick={() => onViewReport(successReportId)}>
              Zobacz zgłoszenie
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="flex min-h-0 flex-col">
          <div className="border-b border-primary-dark-300/65 px-3 py-2.5 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
                  Product Feedback
                </p>
                <h2 id={titleId} className="mt-1 text-lg font-semibold text-primary-light-100">
                  Zgłoś problem lub sugestię
                </h2>
              </div>
              <button
                type="button"
                onClick={requestClose}
                disabled={pending}
                className="min-h-11 min-w-11 rounded-lg text-primary-light-300 hover:bg-primary-dark-100/60 hover:text-primary-light-100 disabled:opacity-50"
                aria-label="Zamknij formularz"
              >
                ×
              </button>
            </div>
          </div>

          <div
            data-testid="product-feedback-scroll-region"
            className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 sm:space-y-4 sm:p-5"
          >
            {message && (
              <div role="alert">
                <Alert variant="error">
                  <p>{message}</p>
                  {attachmentFailure && (
                    <p className="mt-2 text-xs">
                      Etap: {attachmentFailure.stage}
                      <br />
                      Kod: {attachmentFailure.code}
                    </p>
                  )}
                </Alert>
              </div>
            )}

            {pendingStage && pendingStage !== 'preparing' && (
              <p role="status" className="text-sm text-primary-light-300">
                {PENDING_LABELS[pendingStage]}
              </p>
            )}

            <label className="block text-sm font-medium text-primary-light-200">
              Kategoria <span aria-hidden="true">*</span>
              <select
                ref={categoryRef}
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value as ProductFeedbackCategory | '');
                  setErrors((current) => ({ ...current, category: undefined }));
                }}
                disabled={attemptLocked || pending}
                aria-invalid={Boolean(errors.category)}
                className={fieldClassName}
              >
                <option value="">Wybierz kategorię</option>
                {PRODUCT_FEEDBACK_CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.category && (
                <span className="mt-1 block text-xs text-status-error-500">{errors.category}</span>
              )}
            </label>

            <label className="block text-sm font-medium text-primary-light-200">
              Opis <span aria-hidden="true">*</span>
              <textarea
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setErrors((current) => ({ ...current, description: undefined }));
                  growTextarea(event.currentTarget);
                }}
                onInput={(event) => growTextarea(event.currentTarget)}
                maxLength={PRODUCT_FEEDBACK_LIMITS.maxDescriptionCharacters}
                disabled={attemptLocked || pending}
                rows={2}
                aria-invalid={Boolean(errors.description)}
                data-testid="product-feedback-description"
                className={`${fieldClassName} min-h-[4.5rem] max-h-32 resize-none overflow-y-hidden sm:min-h-[6.5rem] sm:max-h-44`}
              />
              {errors.description && (
                <span className="mt-1 block text-xs text-status-error-500">
                  {errors.description}
                </span>
              )}
            </label>

            <section>
              <button
                type="button"
                aria-expanded={optionalDetailsOpen}
                onClick={() => setOptionalDetailsOpen((value) => !value)}
                className="flex min-h-11 w-full items-center justify-between rounded-lg px-2 text-left text-sm font-medium text-primary-light-200 hover:bg-primary-dark-100/45 sm:hidden"
              >
                <span>
                  {optionalDetailsOpen
                    ? 'Ukryj szczegóły odtworzenia'
                    : 'Dodaj szczegóły odtworzenia'}
                </span>
                {hasOptionalDetails && !optionalDetailsOpen && (
                  <span className="text-xs font-normal text-ratuj-200">Dodano</span>
                )}
              </button>
              {(optionalDetailsOpen || !compact) && (
                <div className="space-y-3 pt-2 sm:grid sm:grid-cols-3 sm:gap-3 sm:space-y-0 sm:pt-0">
                  {[
                    ['Oczekiwane zachowanie', expectedBehavior, setExpectedBehavior],
                    ['Rzeczywiste zachowanie', actualBehavior, setActualBehavior],
                    ['Kroki odtworzenia', reproductionNotes, setReproductionNotes],
                  ].map(([label, value, setter]) => (
                    <label
                      key={label as string}
                      className="block text-sm font-medium text-primary-light-200"
                    >
                      {label as string}
                      <textarea
                        value={value as string}
                        onChange={(event) => {
                          (setter as React.Dispatch<React.SetStateAction<string>>)(
                            event.target.value
                          );
                          growTextarea(event.currentTarget, 112);
                        }}
                        onInput={(event) => growTextarea(event.currentTarget, 112)}
                        maxLength={PRODUCT_FEEDBACK_LIMITS.maxSupportingFieldCharacters}
                        disabled={attemptLocked || pending}
                        rows={2}
                        className={`${fieldClassName} max-h-28 resize-none overflow-y-hidden sm:max-h-44`}
                      />
                    </label>
                  ))}
                </div>
              )}
            </section>

            <section className="rounded-lg bg-primary-dark-100/35 p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="sr-only"
                  disabled={attemptLocked || pending}
                  onChange={(event) => void selectAttachment(event.target.files?.[0])}
                  aria-label="Wybierz zrzut ekranu"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="neutral"
                  disabled={attemptLocked || pending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {attachmentDraft ? 'Wybierz inny zrzut' : 'Dodaj zrzut ekranu'}
                </Button>
                {attachmentDraft && (
                  <Button
                    type="button"
                    size="sm"
                    variant="neutral"
                    disabled={attemptLocked || pending}
                    onClick={removeAttachment}
                  >
                    Usuń zrzut
                  </Button>
                )}
              </div>
              <details className="mt-1.5 text-xs text-primary-light-400">
                <summary className="min-h-6 cursor-pointer py-1">Formaty i prywatność</summary>
                <p className="mt-1">
                  JPEG, PNG lub WebP, maks. 10 MiB. Obraz zostanie lokalnie przygotowany jako JPEG.
                </p>
                <p className="mt-1 text-status-warning-400">
                  Sprawdź obraz — widoczna treść może zawierać dane osobowe lub sekrety.
                </p>
              </details>
              {pendingStage === 'preparing' && (
                <p role="status" className="mt-2 text-xs text-primary-light-300">
                  {PENDING_LABELS.preparing}
                </p>
              )}
              {attachmentDraft && (
                <div className="mt-2 flex min-w-0 items-center gap-3">
                  {attachmentDraft.previewUrl && (
                    <img
                      src={attachmentDraft.previewUrl}
                      alt="Podgląd wybranego zrzutu"
                      className="h-14 w-20 shrink-0 rounded-md object-cover"
                    />
                  )}
                  <div className="min-w-0 text-xs">
                    <p className="truncate text-primary-light-200">{attachmentDraft.file.name}</p>
                    <p className="text-primary-light-400">
                      {Math.ceil(attachmentDraft.file.size / 1024)} KiB
                      {attachmentDraft.processed
                        ? ` → ${Math.ceil(attachmentDraft.processed.blob.size / 1024)} KiB JPEG`
                        : ''}
                    </p>
                    {attachmentDraft.error && (
                      <p role="alert" className="mt-1 text-status-error-500">
                        {attachmentDraft.error}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </section>

            <section className="rounded-lg bg-primary-dark-100/35 p-2.5">
              <div className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-1">
                <label className="flex min-h-11 flex-1 items-center gap-2 text-sm font-medium text-primary-light-200">
                  <input
                    type="checkbox"
                    checked={includeDiagnostics}
                    onChange={(event) => setIncludeDiagnostics(event.target.checked)}
                    disabled={attemptLocked || pending}
                    className="rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-300 focus:ring-ratuj-300"
                  />
                  <span>
                    {includeDiagnostics ? '✓ Dołącz diagnostykę' : 'Diagnostyka wyłączona'}
                  </span>
                </label>
                {diagnosticsPreview.length > 0 && (
                  <button
                    type="button"
                    aria-expanded={diagnosticsPreviewOpen}
                    onClick={() => setDiagnosticsPreviewOpen((value) => !value)}
                    className="min-h-11 px-1 text-sm font-medium text-ratuj-200 underline"
                  >
                    {diagnosticsPreviewOpen ? 'Ukryj podgląd' : 'Podgląd'}
                  </button>
                )}
              </div>
              <div className="mt-1 text-xs text-primary-light-400">
                <p className="text-primary-light-300">
                  {diagnostics.events.length === 0
                    ? 'Brak zarejestrowanych błędów w ostatnich 5 min.'
                    : `${diagnostics.events.length} zdarzeń z ostatnich 5 min.`}
                </p>
                <p className="mt-1 flex flex-wrap gap-x-1.5 gap-y-0.5">
                  <span>
                    {diagnostics.events.length} zdarzeń · {diagnostics.bytes} B
                  </span>
                  <span aria-hidden="true">·</span>
                  <time
                    data-testid="product-feedback-diagnostics-captured-at"
                    dateTime={diagnostics.capturedAtClient}
                  >
                    Stan z {formatSnapshotTime(diagnostics.capturedAtClient)}
                  </time>
                  <span aria-hidden="true">·</span>
                  <span
                    data-testid="product-feedback-runtime-summary"
                    data-captured-at={runtime.capturedAtClient}
                    className="break-all"
                  >
                    {runtime.routePath} · {runtime.viewport.width}×{runtime.viewport.height}
                  </span>
                </p>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <Button
                  type="button"
                  size="sm"
                  variant="neutral"
                  disabled={!includeDiagnostics || pending || attemptLocked}
                  onClick={refreshSnapshot}
                  className="!min-h-11"
                >
                  Zbierz stan teraz
                </Button>
                <button
                  type="button"
                  disabled={pending || attemptLocked}
                  onClick={checkDiagnostics}
                  className="min-h-11 rounded-lg px-1 text-sm font-medium text-primary-light-300 underline decoration-primary-light-400/60 underline-offset-4 hover:text-primary-light-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Sprawdź diagnostykę
                </button>
              </div>
              {diagnosticsHealth === 'healthy' && (
                <p role="status" className="mt-1 text-xs font-medium text-status-success-400">
                  Rejestrator działa
                </p>
              )}
              {diagnosticsHealth === 'unhealthy' && (
                <p role="status" className="mt-1 text-xs text-status-warning-400">
                  Nie udało się potwierdzić działania diagnostyki. Raport nadal możesz wysłać.
                </p>
              )}
              {diagnosticsPreviewOpen && diagnosticsPreview.length > 0 && (
                <div className="mt-2 max-h-64 space-y-3 overflow-y-auto">
                  {diagnosticsPreview.map((event) => (
                    <article
                      key={event.key}
                      className="rounded-md bg-primary-dark-200/70 p-3 text-xs"
                    >
                      <p className="font-semibold text-primary-light-200">{event.heading}</p>
                      <p className="mt-1 break-words text-primary-light-400">{event.meta}</p>
                      <p className="mt-2 whitespace-pre-wrap break-words text-primary-light-300">
                        {event.message}
                      </p>
                      {event.details.map((detail, index) => (
                        <p
                          key={index}
                          className="mt-1 whitespace-pre-wrap break-words text-primary-light-400"
                        >
                          {detail}
                        </p>
                      ))}
                      {event.stack && (
                        <pre className="mt-2 whitespace-pre-wrap break-words text-primary-light-400">
                          {event.stack}
                        </pre>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className="border-t border-primary-dark-300/65 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:p-5">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {attemptLocked && !pending && !savedReportId && (
                <Button type="button" variant="neutral" onClick={unlockForEditing}>
                  Wróć do edycji jako nowe zgłoszenie
                </Button>
              )}
              <Button type="button" variant="neutral" disabled={pending} onClick={requestClose}>
                {savedReportId ? 'Zakończ' : 'Anuluj'}
              </Button>
              {savedReportId && (
                <Button type="button" variant="neutral" onClick={() => onViewReport(savedReportId)}>
                  Zobacz zgłoszenie
                </Button>
              )}
              <Button type="submit" disabled={pending} isLoading={pending}>
                {savedReportId
                  ? 'Ponów potwierdzenie zrzutu'
                  : createResult
                    ? 'Ponów odczyt raportu'
                    : submissionInput
                      ? 'Spróbuj ponownie'
                      : 'Wyślij zgłoszenie'}
              </Button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};
